import {Component, OnInit} from "@angular/core";
import {SharedService} from "../../services/sharedService";
import {AdminService} from "../../services/admin.service";
import {ToastrService} from "ngx-toastr";
import {DocumentService} from "../../services/document.service";
import {forkJoin, of} from "rxjs";
import {catchError, tap} from "rxjs/operators";
import {saveAs} from 'file-saver';

const KEY = 'HOME_HELP_TEXT';
const DOC_ENTITY = 'help';
const DOC_ENTITY_ID = 'global';

@Component({
    selector: 'app-help-editor',
    template: `
        <div class="container-fluid">

            <div class="spinner-border" role="status" *ngIf="!loaded">
                <span class="sr-only"></span>
            </div>

            <app-template-editor [elementId]="'helpEditorId'" *ngIf="loaded" [height]="'45vh'" style="z-index:0"
                                 (onEditorKeyup)="keyupHandlerFunction($event)" [tempValue]="data">
            </app-template-editor>
            <br/>
            <button class="btn btn-outline-primary btn-lg" (click)="save()" [disabled]="!loaded">
                <span class="glyphicon glyphicon-save"></span> {{ 'ADMIN.SAVE' | translate }}
            </button>

            <hr/>
            <h5>{{ 'ADMIN.ATTACHMENTS' | translate }}</h5>

            <input type="file" #fileInput style="display:none" multiple
                   accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*"
                   (change)="onFilesSelected($event)">

            <button class="btn btn-outline-secondary btn-sm mb-2" (click)="fileInput.click()">
                + {{ 'ADMIN.ATTACH_FILE' | translate }}
            </button>

            <table class="table table-sm" *ngIf="documents.length > 0">
                <thead>
                    <tr>
                        <th>{{ 'ADMIN.FILE_NAME' | translate }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let doc of documents">
                        <td>{{ doc.originalname }}</td>
                        <td class="text-right">
                            <button class="btn btn-sm btn-outline-secondary mr-1" (click)="downloadDocument(doc)">
                                {{ 'ADMIN.DOWNLOAD' | translate }}
                            </button>
                            <button class="btn btn-sm btn-outline-primary mr-1" (click)="insertLink(doc)">
                                {{ 'ADMIN.INSERT_LINK' | translate }}
                            </button>
                            <button class="btn btn-sm btn-outline-danger" (click)="deleteDocument(doc)">
                                {{ 'ADMIN.DELETE' | translate }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>

        </div>
    `
})
export class HelpEditorComponent implements OnInit {

    data: string;
    savedOk: string = '';
    loaded = false;
    adminSettings: any = {};
    documents: any[] = [];

    constructor(private settings: AdminService,
                private shared: SharedService,
                private alertService: ToastrService,
                private documentService: DocumentService) {
        this.shared.getLan$().subscribe(lan => {
            console.debug('Language changed to -> ', lan);
            this.loadHelpText();
        });
    }

    ngOnInit(): void {
        this.loadHelpText();
        this.loadDocuments();
    }

    loadHelpText() {
        this.loaded = false;
        this.settings.getAdminSettings().subscribe(settings => {
            if (settings && settings.length > 0)
                this.adminSettings = settings[0];
            else
                this.adminSettings = {};
            this.data = this.adminSettings[KEY]?.[this.shared.getLan()] || '';
            this.loaded = true;
        });
    }

    loadDocuments() {
        this.documentService.list$(DOC_ENTITY, DOC_ENTITY_ID).subscribe(
            docs => { this.documents = docs || []; },
            () => {
                this.documents = [];
                this.alertService.error('Failed to load attachments');
            }
        );
    }

    onFilesSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files || !input.files.length) return;
        const files = Array.from(input.files);
        input.value = '';
        forkJoin(files.map(file =>
            this.documentService.add$(DOC_ENTITY, DOC_ENTITY_ID, file).pipe(
                tap(() => this.alertService.success(`${file.name} uploaded successfully`)),
                catchError(() => {
                    this.alertService.error(`Failed to upload ${file.name}`);
                    return of(null);
                })
            )
        )).subscribe(() => this.loadDocuments());
    }

    insertLink(doc: any) {
        const editor = tinymce.get('helpEditorId');
        if (editor && doc?.id && doc?.originalname) {
            const safeId = String(doc.id).replace(/[^a-zA-Z0-9\-_]/g, '');
            const safeName = doc.originalname
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            editor.insertContent(`<a href="#" data-document-id="${safeId}">${safeName}</a>`);
        }
    }

    downloadDocument(doc: any) {
        this.documentService.get$(doc.id).subscribe(
            (blob: Blob) => { saveAs(blob, doc.originalname); },
            () => { this.alertService.error(`Failed to download ${doc.originalname}`); }
        );
    }

    deleteDocument(doc: any) {
        if (!confirm(`Are you sure you want to delete "${doc.originalname}"?`)) return;
        this.documentService.delete$(doc.id).subscribe(
            () => {
                this.alertService.success(`${doc.originalname} deleted`);
                this.loadDocuments();
            },
            () => { this.alertService.error(`Failed to delete ${doc.originalname}`); }
        );
    }

    save() {
        this.savedOk = '';
        const tempData = {id: this.adminSettings.id};
        const lang = {};
        lang[this.shared.getLan()] = '';
        tempData[KEY] = lang;
        tempData[KEY][this.shared.getLan()] = this.data;
        this.settings.saveAdminSettings(tempData).subscribe(data => {
            this.savedOk = 'Ok';
            this.alertService.success('Help text saved successfully');
            this.adminSettings = data[0];
            console.debug('updated settings "' + KEY + '"');
        });
    }

    keyupHandlerFunction($event) {
        this.data = $event;
    }
}
